import {
  formatDateTime,
  MAIL_SEARCH_FIELDS,
  MAIL_SEARCH_TYPES,
  mailSearchFilters,
  type MailSearch,
} from '@hr/shared';
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownIcon,
  CheckCheckIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  CornerDownRightIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FolderTreeIcon,
  ForwardIcon,
  HistoryIcon,
  KeyboardIcon,
  MailIcon,
  MailPlusIcon,
  MailOpenIcon,
  MessagesSquareIcon,
  MinusIcon,
  PenLineIcon,
  PaperclipIcon,
  PenSquareIcon,
  RefreshCwIcon,
  ReplyAllIcon,
  ReplyIcon,
  SendIcon,
  StarIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Field } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page';
import { RichTextField } from '@/components/rich-text';
import { SearchInput } from '@/components/search-input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { DropOverlay, FileChip } from '@/features/mail/attachments';
import { ComposeWindow, SaveState, type ComposeDraft } from '@/features/mail/compose';
import { useDraftAutosave } from '@/features/mail/draft';
import { MailAccountContext, useMailAccount, type NavAccount } from '@/features/mail/account';
import { AccountDialog } from '@/features/mail/account-dialog';
import { FileIcon } from '@/features/mail/file-icon';
import { FolderDialog } from '@/features/mail/folder-dialog';
import { FolderPanel } from '@/features/mail/folder-panel';
import { MailNav } from '@/features/mail/mail-nav';
import { folderTree } from '@/features/mail/folder-tree';
import { fileSize } from '@/features/mail/file-kind';
import { WidthSplitter } from '@/features/mail/list-splitter';
import {
  FOLDERS_WIDTH,
  LIST_WIDTH,
  useStoredFlag,
  useStoredWidth,
} from '@/features/mail/list-width';
import { SearchChips, SearchOptionsButton } from '@/features/mail/search-options';
import { MoveMenu } from '@/features/mail/move-menu';
import { RecipientInput, RecipientSummary } from '@/features/mail/recipients';
import { MAX_MB, sendMail, splitAddresses } from '@/features/mail/send';
import { SignatureDialog, SignaturePicker } from '@/features/mail/signature';
import { useFileDrop } from '@/features/mail/use-file-drop';
import { usePreview } from '@/features/preview/use-preview';
import { uk } from '@/i18n/uk';
import { errorMessage, trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/types';
import { useDebounced } from '@/lib/use-debounced';
import { cn } from '@/lib/utils';

type Folder = RouterOutputs['mail']['folders'][number];
type Thread = RouterOutputs['mail']['threads']['items'][number];
type Message = RouterOutputs['mail']['thread'][number];
type Ref = { folder: string; uid: number };
type Person = { name: string; address: string };

/** «Вхідні» / «Надіслані» — ті самі розмови-чати, але лише з листами цієї папки. */
const SCOPE_INBOX = ':inbox';
const SCOPE_SENT = ':sent';
const PAGE = 40;
/**
 * Сітка пошти: панель папок, список розмов (тягнуть), розмова. Розмові лишається щонайменше
 * 24rem: список не ширший, ніж дозволяє решта.
 */
const MAIL_GRID =
  'grid h-[calc(100svh-14.5rem)] min-h-[28rem] gap-4 lg:h-[calc(100svh-11.5rem)] lg:min-h-[32rem] lg:grid-cols-[var(--folders-w)_minmax(16rem,min(var(--list-w),calc(100%_-_var(--folders-w)_-_26rem)))_minmax(0,1fr)]';
/** Ширина згорнутої панелі папок (смуга з іконками), px. */
const RAIL_WIDTH = 48;

/** Ширини колонок (панель папок і список) — з пам'яті браузера. */
function useMailColumns() {
  const list = useStoredWidth(LIST_WIDTH);
  const panel = useStoredWidth(FOLDERS_WIDTH);
  const [rail, setRail] = useStoredFlag('mail.folders.rail');
  const panelWidth = rail ? RAIL_WIDTH : panel.width;
  const style = {
    '--list-w': `${list.width}px`,
    '--folders-w': `${panelWidth}px`,
  } as CSSProperties;
  return { list, panel, rail, setRail, panelWidth, style };
}

export const Route = createFileRoute('/_app/mail')({
  validateSearch: z.object({
    /** Розмова: Message-ID першого листа. */
    thread: z.string().max(1000).optional().catch(undefined),
    /** Інша папка (Спам, Кошик…); без неї — «Вхідні» + «Надіслані». */
    box: z.string().max(500).optional().catch(undefined),
    q: z.string().max(200).optional().catch(undefined),
    /** Конкретний лист (посилання з картки): відкривається його розмова. */
    folder: z.string().max(500).optional().catch(undefined),
    uid: z.number().int().positive().optional().catch(undefined),
    /** Чернетка відповіді, яку відкрили з «Чернеток», — дописати в розмові. */
    draft: z.number().int().positive().optional().catch(undefined),
    /** Лише непрочитані / лише позначені «важливими». */
    filter: z.enum(['unread', 'flagged']).optional().catch(undefined),
    /** Тонке налаштування пошуку (див. MailSearch). */
    fields: z.array(z.enum(MAIL_SEARCH_FIELDS)).min(1).optional().catch(undefined),
    type: z.enum(MAIL_SEARCH_TYPES).optional().catch(undefined),
    after: z.iso.date().optional().catch(undefined),
    before: z.iso.date().optional().catch(undefined),
    attachments: z.boolean().optional().catch(undefined),
    larger: z.number().int().min(1).max(100).optional().catch(undefined),
    everywhere: z.boolean().optional().catch(undefined),
    sort: z.enum(['newest', 'oldest']).optional().catch(undefined),
    /** Додаткова скринька (рішення 032); без неї — корпоративна. */
    acc: z.uuid().optional().catch(undefined),
  }),
  component: MailPage,
});

/** Налаштування пошуку з параметрів адреси — порожні не передаються. */
function searchOf(s: MailSearch): MailSearch {
  const { fields, type, after, before, attachments, larger, everywhere, sort } = s;
  return { fields, type, after, before, attachments, larger, everywhere, sort };
}
const NO_SEARCH: MailSearch = searchOf({});

function folderLabel(f: Folder) {
  return (f.role && uk.mail.roles[f.role]) || f.name;
}

// Явний тип: інакше тип маршруту виводиться з компонента, який сам читає Route.useSearch().
function MailPage(): ReactNode {
  const t = uk.mail;
  const status = useQuery(trpc.mail.status.queryOptions());
  if (status.isPending) return <MailSkeleton />;
  if (!status.data?.ready) {
    return (
      <>
        <PageHeader title={t.title} />
        <ConnectMine status={status.data ?? null} />
      </>
    );
  }
  return <Mailbox address={status.data.address!} />;
}

/** Поки невідомо, чи підключена скринька, — макет сторінки пошти, щоб нічого не стрибало. */
function MailSkeleton() {
  const columns = useMailColumns();
  return (
    <div>
      <PageHeader
        title={uk.mail.title}
        description={<Skeleton className="h-4 w-40" />}
        actions={<Skeleton className="h-9 w-72" />}
      />
      <div style={columns.style} className={MAIL_GRID}>
        <div className="hidden space-y-2 lg:block">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="h-7" />
          ))}
        </div>
        <div className="flex min-h-0 flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-8 w-56" />
          <div className="min-h-0 flex-1 overflow-hidden rounded-lg border">
            <ThreadListSkeleton />
          </div>
        </div>
        <div className="hidden rounded-lg border lg:block" />
      </div>
    </div>
  );
}

/** Рядки-заглушки списку розмов тієї ж висоти, що й справжні. */
function ThreadListSkeleton() {
  return (
    <ul className="divide-y" aria-hidden>
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="flex gap-3 px-3 py-2.5">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex-1 space-y-1.5 py-0.5">
            <div className="flex justify-between gap-4">
              <Skeleton className="h-3.5" style={{ width: `${45 + ((i * 17) % 35)}%` }} />
              <Skeleton className="h-3 w-10" />
            </div>
            <Skeleton className="h-3.5" style={{ width: `${55 + ((i * 23) % 40)}%` }} />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ConnectMine({ status }: { status: RouterOutputs['mail']['status'] | null }) {
  const t = uk.mail;
  const qc = useQueryClient();
  const [address, setAddress] = useState(status?.address ?? '');
  const [password, setPassword] = useState('');
  const [other, setOther] = useState(false);
  const opts = {
    onSuccess: () => {
      toast.success(t.connected);
      void qc.invalidateQueries(trpc.mail.pathFilter());
      void qc.invalidateQueries(trpc.mailboxes.pathFilter());
    },
    onError: (e: unknown) => toast.error(errorMessage(e)),
  };
  const connect = useMutation(trpc.mailboxes.connectMine.mutationOptions(opts));
  const update = useMutation(trpc.mailboxes.updateMyPassword.mutationOptions(opts));
  if (status?.blocked) return <EmptyState icon={MailIcon} title={t.blocked} />;
  const existing = Boolean(status?.address);
  return (
    <div className="max-w-md space-y-3 rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{existing ? t.needPassword : t.connectHint}</p>
      <Field label={t.address}>
        <Input
          type="email"
          value={address}
          disabled={existing}
          onChange={(e) => setAddress(e.target.value)}
        />
      </Field>
      <Field label={t.password}>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Button
        disabled={!address.includes('@') || !password || connect.isPending || update.isPending}
        onClick={() =>
          existing ? update.mutate({ password }) : connect.mutate({ address, password })
        }
      >
        {t.connect}
      </Button>
      {/* Корпоративної немає чи вона не потрібна — будь-яка інша пошта (рішення 032). */}
      <div className="border-t pt-3">
        <Button variant="outline" onClick={() => setOther(true)}>
          <MailPlusIcon /> {t.accounts.addOther}
        </Button>
      </div>
      {other && (
        <AccountDialog
          onClose={() => setOther(false)}
          onDone={() => void qc.invalidateQueries(trpc.mail.pathFilter())}
        />
      )}
    </div>
  );
}

const same = (a: Ref, b: Ref) => a.folder === b.folder && a.uid === b.uid;
const refKey = (r: Ref) => `${r.folder}:${r.uid}`;
const nameOf = (p: Person) => p.name || p.address;
const time = (d: Date | string) => formatDateTime(d).slice(11);
const day = (d: Date | string) => formatDateTime(d).slice(0, 10);

/** «Сьогодні» / «Вчора» / дата — для роздільників і списку розмов. */
function dayLabel(d: Date | string) {
  const t = uk.mail;
  const today = day(new Date());
  const yesterday = day(new Date(Date.now() - 86_400_000));
  const value = day(d);
  return value === today ? t.today : value === yesterday ? t.yesterday : value;
}

const AVATAR_COLORS = [
  'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-100',
  'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100',
  'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
  'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-100',
  'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-100',
  'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100',
];

function Avatar({ person, className }: { person: Person | null; className?: string }) {
  const label = person ? nameOf(person) : '?';
  const initials = label
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
  let hash = 0;
  for (const c of person?.address ?? '') hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
        AVATAR_COLORS[hash % AVATAR_COLORS.length],
        className,
      )}
    >
      {initials || '?'}
    </span>
  );
}

type ThreadsData = RouterOutputs['mail']['threads'];

/** Дії з клавіатури у відкритій розмові — їх реєструє розмова. */
interface ConversationKeys {
  reply: () => void;
  replyAll: () => void;
  forward: () => void;
  trash: () => void;
}

/** Курсор у полі вводу, редакторі чи вікні — клавіші належать їм. */
const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  Boolean(
    el.closest(
      'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]',
    ),
  );

/** Тонка смужка завантаження вгорі списку (зміна папки, пошук, «Оновити»). */
function LoadingBar({ show }: { show: boolean }) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden transition-opacity duration-300',
        show ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className="h-full w-1/3 animate-loading-bar rounded-full bg-primary" />
    </div>
  );
}

/** Знайдене пошуком — підсвічено. */
function Highlight({ text, q }: { text: string; q?: string }) {
  const needle = q?.trim().toLowerCase();
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (!needle || at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-amber-200/80 text-inherit dark:bg-amber-500/30">
        {text.slice(at, at + needle.length)}
      </mark>
      {text.slice(at + needle.length)}
    </>
  );
}

/** Прапорець «вибрати всі»: порожній, частково (риска) або всі (галочка). */
function PickBox({
  state,
  label,
  disabled,
  onClick,
}: {
  state: 'none' | 'some' | 'all';
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-40"
    >
      <span
        className={cn(
          'flex size-5 items-center justify-center rounded-md border-2 transition-colors',
          state === 'none'
            ? 'border-muted-foreground/40 bg-background'
            : 'border-primary bg-primary text-primary-foreground',
        )}
      >
        {state === 'all' && <CheckIcon className="size-3.5" strokeWidth={3} />}
        {state === 'some' && <MinusIcon className="size-3.5" strokeWidth={3} />}
      </span>
    </button>
  );
}

function ShortcutsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = uk.mail;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.shortcuts}</DialogTitle>
          <DialogDescription>{t.shortcutsHint}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
          {t.shortcutList.map(([keys, what]) => (
            <Fragment key={keys}>
              <dt>
                <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs whitespace-pre shadow-xs">
                  {keys}
                </kbd>
              </dt>
              <dd className="text-muted-foreground">{what}</dd>
            </Fragment>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

function Mailbox({ address: personalAddress }: { address: string }) {
  const t = uk.mail;
  const search = Route.useSearch();
  /** Відкрита скринька: додаткова (id) або корпоративна (undefined). */
  const account = search.acc;
  const accounts = useQuery(trpc.mail.accounts.queryOptions());
  const accountList = accounts.data ?? [];
  // Адреса відкритої скриньки: «мої» листи в розмові, шапка сторінки.
  const address =
    (account ? accountList.find((a) => a.id === account) : accountList[0])?.address ??
    personalAddress;
  const [accountDialog, setAccountDialog] = useState(false);
  const [removingAccount, setRemovingAccount] = useState<NavAccount | null>(null);
  const removeAccount = useMutation(
    trpc.mail.removeAccount.mutationOptions({
      onSuccess: (_r, { id }) => {
        toast.success(t.accounts.removed);
        if (id === account) changeView({ acc: undefined, box: undefined });
        void qc.invalidateQueries(trpc.mail.pathFilter());
      },
      onError: (e) => toast.error(errorMessage(e)),
    }),
  );
  const navigate = useNavigate({ from: Route.fullPath });
  const setSearch = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const qc = useQueryClient();
  // Нові листи приходять подією (вебхук хостингу); опитування — запасне, для змін з інших програм.
  const folders = useQuery({
    ...trpc.mail.folders.queryOptions({ account }),
    refetchInterval: 180_000,
  });
  const q = useDebounced(search.q, 400);
  const [limit, setLimit] = useState(PAGE);
  // «Показати ще» — список лишається яскравим; інша папка чи пошук — старий список блідне.
  const [loadingMore, setLoadingMore] = useState(false);
  const threads = useQuery({
    ...trpc.mail.threads.queryOptions({
      account,
      folder: search.box && !search.box.startsWith(':') ? search.box : undefined,
      scope: search.box === SCOPE_INBOX ? 'inbox' : search.box === SCOPE_SENT ? 'sent' : 'all',
      limit,
      q: q || undefined,
      filter: search.filter,
      search: searchOf(search),
    }),
    placeholderData: keepPreviousData,
    refetchInterval: 180_000,
  });
  // Вибрані розмови (для дій над кількома одразу) — лише з тих, що в списку.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = () => {
    setRefreshing(true);
    void qc.invalidateQueries(trpc.mail.pathFilter()).finally(() => setRefreshing(false));
  };
  /** Інша папка, пошук чи фільтр: список — з початку, вибрані розмови — скинуті. */
  const changeView = (patch: Partial<typeof search>) => {
    setLimit(PAGE);
    setLoadingMore(false);
    setPicked(new Set());
    setSearch({ thread: undefined, folder: undefined, uid: undefined, draft: undefined, ...patch });
  };
  // Вікно листа: `id` — щоб нове вікно (інша чернетка) починалось з чистого стану.
  const [compose, setCompose] = useState<{ id: number; draft: ComposeDraft } | null>(null);
  const openCompose = (draft: ComposeDraft) => setCompose((c) => ({ id: (c?.id ?? 0) + 1, draft }));
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const area = useRef<HTMLDivElement>(null);
  const columns = useMailColumns();
  const grid = useRef<HTMLDivElement>(null);
  const [foldersSheet, setFoldersSheet] = useState(false);
  const searchBox = useRef<HTMLDivElement>(null);
  const conversationKeys = useRef<ConversationKeys | null>(null);
  const draftsPath = folders.data?.find((f) => f.role === 'drafts')?.path;
  const trashPath = folders.data?.find((f) => f.role === 'trash')?.path;
  const inDrafts = Boolean(search.box) && search.box === draftsPath;
  const inTrash = Boolean(search.box) && search.box === trashPath;

  /** Список одразу, не чекаючи сервера (позначки, кошик); оновлення з сервера його підтвердить. */
  const patchThreads = (fn: (th: Thread) => Thread | null) =>
    qc.setQueriesData<ThreadsData>(trpc.mail.threads.pathFilter(), (old) =>
      old
        ? {
            ...old,
            items: old.items.flatMap((th) => {
              const next = fn(th);
              return next ? [next] : [];
            }),
          }
        : old,
    );
  // Розмови, що йдуть у кошик, спершу плавно згортаються, а тоді зникають зі списку.
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const removeThreads = (keys: string[]) => {
    setLeaving((s) => new Set([...s, ...keys]));
    window.setTimeout(() => {
      void patchThreads((th) => (keys.includes(th.key) ? null : th));
      setLeaving((s) => new Set([...s].filter((k) => !keys.includes(k))));
    }, 200);
  };

  const settle = {
    onError: (e: unknown) => toast.error(errorMessage(e)),
    onSettled: () => qc.invalidateQueries(trpc.mail.pathFilter()),
  };
  const seenMany = useMutation(trpc.mail.setSeen.mutationOptions(settle));
  const flagMany = useMutation(trpc.mail.setFlagged.mutationOptions(settle));
  const trashMany = useMutation(
    trpc.mail.trash.mutationOptions({
      ...settle,
      onSuccess: (r, v) =>
        toast.success(r.deleted ? t.deleted(r.deleted) : t.trashedMany(v.refs.length)),
    }),
  );
  const removeDraft = useMutation(
    trpc.mail.deleteDraft.mutationOptions({
      onSuccess: () => toast.success(t.draftDeleted),
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.mail.pathFilter()),
    }),
  );

  const keysOf = (list: Thread[]) => new Set(list.map((th) => th.key));
  /** Прочитано / непрочитано: непрочитаним стає останній лист розмови. */
  const markSeen = (list: Thread[], seen: boolean) => {
    if (!list.length) return;
    const keys = keysOf(list);
    void patchThreads((th) =>
      keys.has(th.key) ? { ...th, unread: seen ? 0 : Math.max(1, th.unread) } : th,
    );
    seenMany.mutate({
      account,
      refs: (seen ? list.flatMap((th) => th.refs) : list.map((th) => th.refs.at(-1)!)).slice(
        0,
        500,
      ),
      seen,
    });
  };
  /** «Важливе»: позначається останній лист розмови; знімається — з усіх. */
  const flagThreads = (list: Thread[], flagged: boolean) => {
    if (!list.length) return;
    const keys = keysOf(list);
    void patchThreads((th) => (keys.has(th.key) ? { ...th, flagged } : th));
    flagMany.mutate({
      account,
      refs: (flagged ? list.map((th) => th.refs.at(-1)!) : list.flatMap((th) => th.refs)).slice(
        0,
        500,
      ),
      flagged,
    });
  };

  /** Дописати збережену чернетку у вікні листа. */
  /**
   * Чернетка відповіді — відкриваємо її розмову (там вона дописується в полі відповіді);
   * чернетка нового листа або відповіді на лист, якого вже немає, — у вікні листа.
   */
  async function openDraft(uid: number) {
    if (compose?.draft.draftUid === uid) return;
    try {
      const d = await qc.query({ ...trpc.mail.draft.queryOptions({ account, uid }), staleTime: 0 });
      if (!d) throw new Error(t.draftOpenFailed);
      const parent = d.inReplyTo
        ? await qc.query({
            ...trpc.mail.findMessage.queryOptions({ account, messageId: d.inReplyTo }),
            staleTime: 0,
          })
        : null;
      if (parent) {
        changeView({
          box: undefined,
          filter: undefined,
          folder: parent.folder,
          uid: parent.uid,
          draft: uid,
        });
        return;
      }
      openCompose({
        to: d.to.join(', '),
        cc: d.cc.join(', '),
        bcc: d.bcc.join(', '),
        subject: d.subject,
        html: d.html,
        quote: d.quoteHtml ?? undefined,
        quoteHeader: d.quotePreview?.header,
        quotePreview: d.quotePreview?.html,
        inReplyTo: d.inReplyTo ?? undefined,
        references: d.references,
        draftUid: d.uid,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t.draftOpenFailed);
    }
  }

  const anchor = search.folder && search.uid ? { folder: search.folder, uid: search.uid } : null;
  const fresh = threads.data?.items ?? [];
  // Відкрита розмова лишається у списку на своєму місці, поки її не закрили: у «Непрочитаних»
  // вона, відкрившись, стає прочитаною і зі списку з сервера зникає — вікно не має закриватись.
  const [kept, setKept] = useState<{ thread: Thread; index: number } | null>(null);
  const liveIndex = search.thread ? fresh.findIndex((x) => x.key === search.thread) : -1;
  if (liveIndex >= 0 && (kept?.thread !== fresh[liveIndex] || kept?.index !== liveIndex)) {
    setKept({ thread: fresh[liveIndex]!, index: liveIndex });
  } else if (!search.thread && kept) {
    setKept(null);
  }
  const items =
    liveIndex < 0 && kept && kept.thread.key === search.thread
      ? [...fresh.slice(0, kept.index), { ...kept.thread, unread: 0 }, ...fresh.slice(kept.index)]
      : fresh;
  const selected =
    (search.thread ? items.find((x) => x.key === search.thread) : undefined) ??
    (anchor ? items.find((x) => x.refs.some((r) => same(r, anchor))) : undefined);
  // Лист із картки поза завантаженими розмовами — показуємо його одного.
  const refs = selected?.refs ?? (anchor && threads.data ? [anchor] : null);
  const open = Boolean(refs);
  const mainUnseen = (folders.data ?? []).find((f) => f.role === 'inbox')?.unseen ?? 0;
  // Папки — панеллю-деревом ліворуч (рішення 031).
  const byFolderPath = new Map((folders.data ?? []).map((f) => [f.path, f]));
  /** Повна назва: «Проєкти / Звіти», «Надіслані / тест». */
  const fullLabel = (f: Folder): string => {
    const parent = f.parent ? byFolderPath.get(f.parent) : undefined;
    return parent ? `${fullLabel(parent)} / ${f.name}` : folderLabel(f);
  };
  const customFolders = (folders.data ?? [])
    .filter((f) => f.custom)
    .map((f) => ({ ...f, label: fullLabel(f) }));
  const allFolders = (folders.data ?? []).map((f) => ({ ...f, label: fullLabel(f) }));
  const nameOf = (f: Folder) => (f.custom ? f.name : folderLabel(f));
  // Куди перенести: власні папки, «Архів», «Спам»; куди вкласти нову — будь-яка, крім службових.
  const moveTree = folderTree(
    folders.data ?? [],
    nameOf,
    (f) => f.custom || f.role === 'archive' || f.role === 'junk',
  );
  const parentTree = folderTree(folders.data ?? [], nameOf, (f) => f.custom || f.role !== null);
  /** Значення `box` для папки: «Вхідні» й «Надіслані» — розмови з їхніми листами. */
  const boxOf = (f: Folder) =>
    f.role === 'inbox' ? SCOPE_INBOX : f.role === 'sent' ? SCOPE_SENT : f.path;
  const currentFolderName = !search.box
    ? t.allMail
    : search.box === SCOPE_INBOX
      ? (t.roles.inbox ?? '')
      : search.box === SCOPE_SENT
        ? (t.roles.sent ?? '')
        : (allFolders.find((f) => f.path === search.box)?.label ?? search.box);
  /** Перейти в іншу скриньку (і далі, можливо, відкрити вікно папки — уже в ній). */
  const openAccount = (acc: string | undefined, box?: string) => {
    if (acc !== account || box !== search.box) changeView({ acc, box });
  };
  const folderProps = (acc: string | undefined) => ({
    boxOf,
    nameOf,
    onSelect: (box: string | undefined) => {
      openAccount(acc, box);
      setFoldersSheet(false);
    },
    onCreate: (parent?: string) => {
      openAccount(acc, acc === account ? search.box : undefined);
      setFolderDialog({ parent });
    },
    onEdit: (path: string) => {
      openAccount(acc, path);
      setFolderDialog({ path });
    },
    onDelete: (path: string) => {
      openAccount(acc, acc === account ? search.box : undefined);
      setFolderDialog({ path, startDelete: true });
    },
    onReorder: (paths: string[]) => reorderFolders.mutate({ account: acc, paths }),
  });
  const folderPanel = (mode: 'panel' | 'rail' | 'sheet') =>
    mode === 'rail' ? (
      <FolderPanel
        {...folderProps(account)}
        folders={folders.data ?? []}
        active={search.box}
        rail
        onToggleRail={() => columns.setRail(false)}
      />
    ) : (
      <MailNav<Folder>
        accounts={accountList}
        current={account}
        box={search.box}
        folderProps={folderProps}
        onOpenAccount={(acc) => {
          openAccount(acc, undefined);
          setFoldersSheet(false);
        }}
        onCreate={() => setFolderDialog({})}
        onAddAccount={() => setAccountDialog(true)}
        onRemoveAccount={(id) => setRemovingAccount(accountList.find((a) => a.id === id) ?? null)}
        onToggleRail={mode === 'sheet' ? undefined : () => columns.setRail(true)}
      />
    );
  const activeTab = customFolders.some((f) => f.path === search.box) ? search.box : undefined;
  const inboxPath = folders.data?.find((f) => f.role === 'inbox')?.path;
  const sentPath = folders.data?.find((f) => f.role === 'sent')?.path;
  /** Що переносити: надіслані й чернетки лишаються на місці. */
  const movable = (rs: Ref[]) => rs.filter((x) => x.folder !== sentPath && x.folder !== draftsPath);
  /** Єдиний співрозмовник — для «Завжди від …». */
  const senderOf = (ths: Thread[]) =>
    ths.length === 1 && ths[0]!.participants.length === 1
      ? ths[0]!.participants[0]!.address
      : undefined;
  const [folderDialog, setFolderDialog] = useState<{
    path?: string;
    /** Нова вкладена папка — усередині цієї. */
    parent?: string;
    /** Одразу спитати про видалення. */
    startDelete?: boolean;
    /** Після створення папки перенести в неї ці листи («Нова папка…» з меню переміщення). */
    moveRefs?: Ref[];
    initial?: { from: string[] };
  } | null>(null);
  const moveInto = useMutation(
    trpc.mail.move.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(trpc.mail.pathFilter()),
      onError: (e) => toast.error(errorMessage(e)),
    }),
  );
  const reorderFolders = useMutation(
    trpc.mail.reorderFolders.mutationOptions({
      onMutate: ({ paths, account: acc }) => {
        qc.setQueryData(trpc.mail.folders.queryKey({ account: acc }), (old) =>
          old
            ? [
                ...old.filter((f) => !f.custom),
                ...paths.map((p) => old.find((f) => f.path === p)!).filter(Boolean),
              ]
            : old,
        );
      },
      onError: (e) => {
        toast.error(errorMessage(e));
        void qc.invalidateQueries(trpc.mail.folders.pathFilter());
      },
    }),
  );

  const pickedThreads = items.filter((th) => picked.has(th.key));
  const togglePick = (key: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const allPicked = items.length > 0 && pickedThreads.length === items.length;
  const pickAll = () => setPicked(allPicked ? new Set() : new Set(items.map((th) => th.key)));
  // Кошик — видалення назавжди, лише після підтвердження.
  const [confirmBulk, setConfirmBulk] = useState(false);
  const trashPicked = () => {
    removeThreads(pickedThreads.map((th) => th.key));
    if (selected && picked.has(selected.key)) back();
    trashMany.mutate({ account, refs: pickedThreads.flatMap((th) => th.refs).slice(0, 500) });
    setPicked(new Set());
  };
  const deletePicked = () => {
    if (inTrash) {
      setConfirmBulk(true);
      return;
    }
    if (inDrafts) {
      const uids = pickedThreads.flatMap((th) => th.refs.map((r) => r.uid));
      // Відкрита у вікні листа — вікно при закритті зберегло б її знову.
      if (compose?.draft.draftUid && uids.includes(compose.draft.draftUid)) {
        toast.info(t.draftOpenInWindow);
        return;
      }
      removeThreads(pickedThreads.map((th) => th.key));
      removeDraft.mutate({ account, uids: uids.slice(0, 200) });
      setPicked(new Set());
      return;
    }
    trashPicked();
  };
  const afterBulk = (fn: () => void) => {
    fn();
    setPicked(new Set());
  };

  const back = () =>
    setSearch({ thread: undefined, folder: undefined, uid: undefined, draft: undefined });
  const openItem = (th: Thread) => {
    if (inDrafts) {
      void openDraft(th.refs.at(-1)!.uid);
      return;
    }
    // Непрочитане стає прочитаним, щойно розмову відкрито (сервер позначить при читанні).
    if (th.unread) void patchThreads((x) => (x.key === th.key ? { ...x, unread: 0 } : x));
    setSearch({ thread: th.key, folder: undefined, uid: undefined, draft: undefined });
  };

  // Клавіатурні скорочення, як у поштових програмах (див. «?»).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      if (document.querySelector('[role="alertdialog"], [role="dialog"][data-state="open"]'))
        return;
      const at = selected ? items.indexOf(selected) : -1;
      const keys = conversationKeys.current;
      const actions: Record<string, (() => void) | undefined> = {
        j: items[at + 1] ? () => openItem(items[at + 1]!) : undefined,
        k: at > 0 ? () => openItem(items[at - 1]!) : undefined,
        Escape: open ? back : undefined,
        c: () => openCompose({}),
        '/': () => searchBox.current?.querySelector('input')?.focus(),
        '?': () => setShortcutsOpen(true),
        s: selected ? () => flagThreads([selected], !selected.flagged) : undefined,
        U: selected
          ? () => {
              markSeen([selected], false);
              back();
            }
          : undefined,
        r: keys?.reply,
        a: keys?.replyAll,
        f: keys?.forward,
        '#': keys?.trash,
        Delete: keys?.trash,
      };
      const action = actions[e.key];
      if (!action) return;
      e.preventDefault();
      action();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Непрочитані — і в назві вкладки браузера.
  useEffect(() => {
    const before = document.title;
    document.title = mainUnseen ? `(${mainUnseen}) ${t.title} · WSDMS` : `${t.title} · WSDMS`;
    return () => {
      document.title = before;
    };
  }, [mainUnseen, t.title]);

  const unreadInList = items.filter((th) => th.unread > 0);
  const dimmed = threads.isPlaceholderData && !loadingMore;

  return (
    <MailAccountContext.Provider value={account}>
      <div ref={area}>
        <PageHeader
          title={t.title}
          description={address}
          actions={
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label={t.shortcuts}
                title={`${t.shortcuts} (?)`}
                className="max-md:hidden"
                onClick={() => setShortcutsOpen(true)}
              >
                <KeyboardIcon />
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label={t.refresh}
                title={t.refresh}
                onClick={refreshAll}
              >
                <RefreshCwIcon className={cn(refreshing && 'animate-spin')} />
              </Button>
              <Button variant="outline" onClick={() => setSignatureOpen(true)}>
                <PenLineIcon /> {t.signature}
              </Button>
              <Button onClick={() => openCompose({})}>
                <PenSquareIcon /> {t.compose}
              </Button>
            </div>
          }
        />
        <div ref={grid} style={columns.style} className={MAIL_GRID}>
          <aside className="relative hidden min-h-0 lg:block">
            {folderPanel(columns.rail ? 'rail' : 'panel')}
            {!columns.rail && (
              <WidthSplitter
                spec={FOLDERS_WIDTH}
                width={columns.panel.width}
                onChange={columns.panel.setWidth}
                // Списку — щонайменше 16rem, розмові — 24rem.
                max={() => (grid.current?.clientWidth ?? Infinity) - 256 - 384 - 32}
                label={uk.mail.folderPanel.resize}
                hint={uk.mail.folderPanel.resizeHint}
              />
            )}
          </aside>
          <section
            className={cn(
              '@container relative flex min-h-0 min-w-0 flex-col gap-2',
              open && 'hidden lg:flex',
            )}
            aria-label={t.conversations}
          >
            <WidthSplitter
              spec={LIST_WIDTH}
              width={columns.list.width}
              onChange={columns.list.setWidth}
              max={() => (grid.current?.clientWidth ?? Infinity) - columns.panelWidth - 384 - 32}
              label={t.resizeList}
              hint={t.resizeListHint}
            />
            {/* Вузький список (його тягнуть): пошук — окремим рядком під папкою. */}
            <div ref={searchBox} className="flex flex-wrap gap-2">
              {/* На телефоні й вузькому екрані панелі немає — папки відкриваються збоку. */}
              <Button
                variant="outline"
                className="w-full justify-start @sm:w-44 @sm:shrink-0 lg:hidden"
                onClick={() => setFoldersSheet(true)}
              >
                <FolderTreeIcon />
                <span className="truncate">{currentFolderName}</span>
              </Button>
              <div className="flex min-w-0 flex-1 basis-full gap-2 @sm:basis-0">
                <SearchInput
                  value={search.q ?? ''}
                  placeholder={t.searchPlaceholder}
                  className="min-w-0 flex-1"
                  onChange={(v) => changeView({ q: v || undefined })}
                />
                <SearchOptionsButton
                  value={searchOf(search)}
                  onChange={(v) => changeView({ ...NO_SEARCH, ...v })}
                />
              </div>
            </div>
            <SearchChips
              value={searchOf(search)}
              onChange={(v) => changeView({ ...NO_SEARCH, ...v })}
            />
            {/* Одна висота і для фільтрів, і для дій з вибраними — список не стрибає. */}
            <div className="flex h-8 shrink-0 items-center gap-1">
              {pickedThreads.length > 0 ? (
                <div className="flex h-full w-full items-center gap-1 rounded-md bg-primary/10 pr-1 duration-150 animate-in fade-in-0">
                  <PickBox
                    state={allPicked ? 'all' : 'some'}
                    label={allPicked ? t.clearSelection : t.selectAll}
                    onClick={pickAll}
                  />
                  <span className="text-xs font-medium tabular-nums">
                    {t.selected(pickedThreads.length)}
                  </span>
                  <div className="ml-auto flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={t.markRead}
                      aria-label={t.markRead}
                      onClick={() => afterBulk(() => markSeen(pickedThreads, true))}
                    >
                      <MailOpenIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={t.markUnreadShort}
                      aria-label={t.markUnreadShort}
                      onClick={() => afterBulk(() => markSeen(pickedThreads, false))}
                    >
                      <MailIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={t.flag}
                      aria-label={t.flag}
                      onClick={() =>
                        afterBulk(() =>
                          flagThreads(pickedThreads, !pickedThreads.every((th) => th.flagged)),
                        )
                      }
                    >
                      <StarIcon />
                    </Button>
                    {!inTrash && !inDrafts && (
                      <MoveMenu
                        className="size-7"
                        folders={customFolders}
                        tree={moveTree}
                        inbox={inboxPath}
                        current={activeTab}
                        refs={movable(pickedThreads.flatMap((th) => th.refs)).slice(0, 500)}
                        allRefs={pickedThreads.flatMap((th) => th.refs).slice(0, 500)}
                        sender={senderOf(pickedThreads)}
                        onMoved={() => setPicked(new Set())}
                        onNewFolder={() =>
                          setFolderDialog({
                            moveRefs: movable(pickedThreads.flatMap((th) => th.refs)).slice(0, 500),
                          })
                        }
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-destructive"
                      title={inTrash ? t.deleteForever : inDrafts ? t.discardDraft : t.toTrash}
                      aria-label={inTrash ? t.deleteForever : inDrafts ? t.discardDraft : t.toTrash}
                      onClick={deletePicked}
                    >
                      <Trash2Icon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      title={t.clearSelection}
                      aria-label={t.clearSelection}
                      onClick={() => setPicked(new Set())}
                    >
                      <XIcon />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <PickBox
                    state="none"
                    label={t.selectAll}
                    disabled={!items.length}
                    onClick={pickAll}
                  />
                  <div role="group" className="flex rounded-md bg-muted p-0.5 text-xs">
                    {(['all', 'unread', 'flagged'] as const).map((f) => {
                      const active = (search.filter ?? 'all') === f;
                      return (
                        <button
                          key={f}
                          type="button"
                          aria-pressed={active}
                          className={cn(
                            'rounded px-2 py-1 font-medium whitespace-nowrap transition-colors @sm:px-2.5',
                            active
                              ? 'bg-background text-foreground shadow-xs'
                              : 'text-muted-foreground hover:text-foreground',
                          )}
                          onClick={() => changeView({ filter: f === 'all' ? undefined : f })}
                        >
                          {t.filters[f]}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-7 px-2 text-xs text-muted-foreground"
                    title={t.markAllRead}
                    aria-label={t.markAllRead}
                    disabled={!unreadInList.length}
                    onClick={() => {
                      markSeen(unreadInList, true);
                      toast.success(t.allReadDone);
                    }}
                  >
                    <CheckCheckIcon /> <span className="@max-sm:hidden">{t.markAllRead}</span>
                  </Button>
                </>
              )}
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border">
              <LoadingBar show={threads.isFetching && (dimmed || refreshing)} />
              <div
                className={cn(
                  'h-full overflow-y-auto transition-opacity duration-200',
                  dimmed && 'opacity-50',
                )}
                aria-busy={threads.isFetching}
              >
                {threads.isPending ? (
                  <ThreadListSkeleton />
                ) : threads.error ? (
                  <p className="p-3 text-sm text-destructive">{errorMessage(threads.error)}</p>
                ) : items.length === 0 ? (
                  <div className="flex h-full items-center justify-center duration-300 animate-in fade-in-0">
                    <EmptyState
                      icon={search.filter === 'flagged' ? StarIcon : MailOpenIcon}
                      title={
                        q || mailSearchFilters(searchOf(search))
                          ? t.emptySearch
                          : search.filter
                            ? t.emptyFiltered[search.filter]
                            : t.empty
                      }
                    />
                  </div>
                ) : (
                  <ul className="divide-y">
                    {items.map((th) => (
                      <ThreadItem
                        key={th.key}
                        thread={th}
                        q={q}
                        active={th === selected}
                        picked={picked.has(th.key)}
                        selecting={pickedThreads.length > 0}
                        leaving={leaving.has(th.key)}
                        isDraft={inDrafts}
                        onPick={() => togglePick(th.key)}
                        onFlag={inDrafts ? undefined : () => flagThreads([th], !th.flagged)}
                        onDelete={
                          inDrafts
                            ? () => {
                                // Усі версії цієї чернетки (згруповані в один рядок) — разом.
                                const uids = th.refs.map((r) => r.uid);
                                // Відкрита у вікні листа — вікно при закритті зберегло б її знову.
                                if (
                                  compose?.draft.draftUid &&
                                  uids.includes(compose.draft.draftUid)
                                ) {
                                  toast.info(t.draftOpenInWindow);
                                  return;
                                }
                                removeThreads([th.key]);
                                removeDraft.mutate({ account, uids });
                              }
                            : undefined
                        }
                        onOpen={() => openItem(th)}
                      />
                    ))}
                  </ul>
                )}
                {threads.data?.hasMore && (
                  <div className="p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      disabled={threads.isFetching}
                      onClick={() => {
                        setLoadingMore(true);
                        setLimit((l) => l + PAGE);
                      }}
                    >
                      {loadingMore && threads.isFetching && (
                        <RefreshCwIcon className="animate-spin" />
                      )}
                      {t.more}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className={cn('h-full min-h-0 min-w-0', !open && 'hidden lg:block')}>
            {refs ? (
              <Conversation
                key={selected?.key ?? refKey(refs[0]!)}
                refs={refs}
                title={selected?.subject}
                unread={selected?.unread ?? 0}
                flagged={selected?.flagged}
                onKeys={(k) => {
                  conversationKeys.current = k;
                }}
                draft={
                  selected?.draft ??
                  (search.draft && draftsPath ? { folder: draftsPath, uid: search.draft } : null)
                }
                myAddress={address}
                onBack={back}
                onFlag={selected ? (v) => flagThreads([selected], v) : undefined}
                onMarkUnread={
                  selected
                    ? () => {
                        markSeen([selected], false);
                        back();
                      }
                    : undefined
                }
                onGone={() => selected && removeThreads([selected.key])}
                onCompose={openCompose}
                onSetupSignature={() => setSignatureOpen(true)}
                moveMenu={
                  !inTrash &&
                  !inDrafts && (
                    <MoveMenu
                      folders={customFolders}
                      tree={moveTree}
                      inbox={inboxPath}
                      current={activeTab}
                      refs={movable(refs)}
                      allRefs={refs}
                      sender={selected ? senderOf([selected]) : undefined}
                      onMoved={(target) => {
                        // Розмова пішла з відкритої вкладки — закриваємо її.
                        if (activeTab && target !== activeTab) back();
                      }}
                      onNewFolder={() => setFolderDialog({ moveRefs: movable(refs) })}
                    />
                  )
                }
              />
            ) : (
              <div className="flex h-full items-center justify-center rounded-lg border">
                <EmptyState icon={MessagesSquareIcon} title={t.pick} />
              </div>
            )}
          </section>
        </div>
        {compose && (
          <ComposeWindow
            key={compose.id}
            draft={compose.draft}
            area={area}
            onClose={() => setCompose(null)}
          />
        )}
        {signatureOpen && (
          <SignatureDialog account={account} onClose={() => setSignatureOpen(false)} />
        )}
        {accountDialog && (
          <AccountDialog
            onClose={() => setAccountDialog(false)}
            onDone={(id) => changeView({ acc: id, box: undefined })}
          />
        )}
        <AlertDialog
          open={Boolean(removingAccount)}
          onOpenChange={(o) => !o && setRemovingAccount(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {removingAccount && t.accounts.removeTitle(removingAccount.address)}
              </AlertDialogTitle>
              <AlertDialogDescription>{t.accounts.removeText}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{uk.common.cancel}</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => removingAccount && removeAccount.mutate({ id: removingAccount.id })}
              >
                {t.accounts.remove}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Sheet open={foldersSheet} onOpenChange={setFoldersSheet}>
          <SheetContent side="left" className="w-72 p-3 pt-10">
            <SheetTitle className="sr-only">{uk.mail.folderPanel.label}</SheetTitle>
            {folderPanel('sheet')}
          </SheetContent>
        </Sheet>
        {folderDialog &&
          (!folderDialog.path || customFolders.some((f) => f.path === folderDialog.path)) && (
            <FolderDialog
              folder={customFolders.find((f) => f.path === folderDialog.path)}
              folders={allFolders}
              tree={parentTree}
              parent={folderDialog.parent}
              startDelete={folderDialog.startDelete}
              initial={folderDialog.initial}
              onClose={() => setFolderDialog(null)}
              onDone={(path) => {
                const refsToMove = folderDialog.moveRefs;
                if (path && refsToMove?.length) {
                  moveInto.mutate({ account, refs: refsToMove, target: path });
                  setPicked(new Set());
                }
                // Нова чи перейменована папка — відкриваємо її; видалена — «Усі листи».
                if (!refsToMove) changeView({ box: path });
              }}
            />
          )}
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t.deleteForever}</AlertDialogTitle>
              <AlertDialogDescription>
                {t.deleteForeverHint(pickedThreads.flatMap((th) => th.refs).length)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{uk.common.cancel}</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={trashPicked}>
                {t.deleteForever}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MailAccountContext.Provider>
  );
}

function ThreadItem({
  thread: th,
  q,
  active,
  picked,
  selecting,
  leaving,
  isDraft,
  onPick,
  onFlag,
  onDelete,
  onOpen,
}: {
  thread: Thread;
  /** Пошуковий запит — підсвітити знайдене. */
  q?: string;
  active: boolean;
  picked: boolean;
  /** Вибрано хоч одну розмову — замість аватарок прапорці вибору. */
  selecting: boolean;
  /** Розмова йде в кошик — згортається. */
  leaving: boolean;
  isDraft: boolean;
  onPick: () => void;
  onFlag?: () => void;
  /** Лише в «Чернетках»: видалити чернетку одразу. */
  onDelete?: () => void;
  onOpen: () => void;
}) {
  const t = uk.mail;
  const who = th.participants.length ? th.participants : th.lastFrom ? [th.lastFrom] : [];
  const unread = th.unread > 0;
  const checkbox = selecting || picked;
  return (
    <li
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
        leaving ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] duration-300 animate-in fade-in-0',
      )}
    >
      <div className="group relative min-h-0 overflow-hidden">
        <button
          type="button"
          onClick={onOpen}
          aria-current={active || undefined}
          className={cn(
            'flex w-full py-2.5 pr-3 pl-15 text-left text-sm transition-colors hover:bg-muted/60',
            active && 'bg-muted hover:bg-muted',
            picked && 'bg-primary/5',
          )}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={cn('min-w-0 flex-1 truncate', unread && 'font-semibold')}>
                <Highlight text={who.map(nameOf).join(', ') || t.onlyMe} q={q} />
              </span>
              <span
                className={cn(
                  'shrink-0 text-xs tabular-nums',
                  unread ? 'font-medium text-primary' : 'text-muted-foreground',
                )}
              >
                {th.lastDate
                  ? dayLabel(th.lastDate) === t.today
                    ? time(th.lastDate)
                    : dayLabel(th.lastDate)
                  : ''}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'min-w-0 flex-1 truncate',
                  unread ? 'font-medium' : 'text-muted-foreground',
                )}
              >
                <Highlight text={th.subject || t.noSubject} q={q} />
              </span>
              {th.attachments && (
                <PaperclipIcon className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              {th.count > 1 && (
                <span
                  className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] leading-4 text-muted-foreground tabular-nums"
                  title={t.count}
                >
                  {th.count}
                </span>
              )}
              {unread && (
                <span className="shrink-0 rounded-full bg-primary px-1.5 text-[11px] leading-4 font-medium text-primary-foreground tabular-nums duration-200 animate-in zoom-in-50">
                  {th.unread}
                </span>
              )}
            </div>
            {/* Третій рядок є завжди — однакова висота рядків. */}
            <div className="h-4 truncate pr-7 text-xs">
              {isDraft || th.draft ? (
                <span className="font-medium text-destructive">{t.draftLabel}</span>
              ) : (
                th.lastFrom && (
                  <span className="text-muted-foreground">
                    {th.lastMine ? t.youLast : t.lastFrom(nameOf(th.lastFrom))}
                  </span>
                )
              )}
            </div>
          </div>
        </button>
        {active && <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-primary" />}
        <button
          type="button"
          role="checkbox"
          aria-checked={picked}
          aria-label={t.select}
          title={t.select}
          onClick={onPick}
          className="absolute top-2.5 left-3 size-9 rounded-full focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <span
            className={cn(
              'block transition-opacity duration-150',
              checkbox ? 'opacity-0' : 'group-hover:opacity-0',
            )}
          >
            <Avatar person={who[0] ?? null} />
          </span>
          <span
            className={cn(
              'absolute inset-0 flex items-center justify-center transition-opacity duration-150',
              checkbox ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <span
              className={cn(
                'flex size-5 items-center justify-center rounded-md border-2 transition-colors',
                picked
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-muted-foreground/40 bg-background',
              )}
            >
              {picked && <CheckIcon className="size-3.5" strokeWidth={3} />}
            </span>
          </span>
        </button>
        {onFlag && (
          <button
            type="button"
            title={th.flagged ? t.unflag : t.flag}
            aria-label={th.flagged ? t.unflag : t.flag}
            aria-pressed={th.flagged}
            onClick={onFlag}
            className={cn(
              'absolute right-1.5 bottom-1 flex size-7 items-center justify-center rounded-md transition-opacity hover:bg-background/80 focus-visible:opacity-100',
              th.flagged ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
            )}
          >
            <StarIcon
              className={cn(
                'size-4 transition-colors',
                th.flagged ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground',
              )}
            />
          </button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1.5 bottom-1 size-7 text-destructive"
            title={t.discardDraft}
            aria-label={t.discardDraft}
            onClick={onDelete}
          >
            <Trash2Icon />
          </Button>
        )}
      </div>
    </li>
  );
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmt = (list: Person[]) =>
  list.map((a) => (a.name ? `${a.name} <${a.address}>` : a.address)).join(', ');

/** Цитата для інших поштових програм; у нашому чаті вона прихована. */
function quote(m: Message, header: string) {
  const body =
    m.fullHtml ?? m.html ?? `<p>${esc(m.fullText ?? m.text ?? '').replace(/\n/g, '<br>')}</p>`;
  return `<p></p><p>${esc(header)}</p><blockquote>${body}</blockquote>`;
}
const header = (m: Message) => uk.mail.wrote(m.date ? formatDateTime(m.date) : '', fmt(m.from));

/**
 * Кому відповідати — як у поштових програмах:
 * «Відповісти» — лише відправнику (на власний лист — тим, кому його надіслали);
 * «Відповісти всім» — у «Кому» відправник і всі з «Кому» листа, у «Копії» — його «Копія».
 * Себе й повтори прибираємо.
 */
function replyRecipients(m: Message, me: string, all: boolean) {
  const sender = (m.mine ? m.to : m.replyTo.length ? m.replyTo : m.from).map((a) => a.address);
  const uniq = (list: string[], skip: string[] = []) =>
    list.filter((a, i) => a && a !== me && !skip.includes(a) && list.indexOf(a) === i);
  if (!all) return { to: uniq(sender), cc: [] };
  const to = uniq([...sender, ...(m.mine ? [] : m.to.map((a) => a.address))]);
  return {
    to,
    cc: uniq(
      m.cc.map((a) => a.address),
      to,
    ),
  };
}

/** Розмова вантажиться: заголовок (з рядка списку) — одразу, листи — заглушками. */
function ConversationSkeleton({ title, onBack }: { title?: string; onBack: () => void }) {
  const t = uk.mail;
  return (
    <div className="flex h-full min-h-96 flex-col rounded-lg border" aria-busy>
      <header className="flex items-start gap-2 border-b px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label={t.back}
          onClick={onBack}
        >
          <ChevronLeftIcon />
        </Button>
        <div className="min-w-0 flex-1">
          {title !== undefined ? (
            <h2 className="truncate font-semibold">{title || t.noSubject}</h2>
          ) : (
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}
          <div className="flex h-4 items-center">
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-4 overflow-hidden bg-muted/30 px-3 py-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className={cn('flex gap-2', i % 2 === 1 && 'flex-row-reverse')}>
            <Skeleton className="hidden size-9 shrink-0 rounded-full sm:block" />
            <Skeleton className={cn('h-28 rounded-2xl', i % 2 ? 'w-1/2' : 'w-2/3')} />
          </div>
        ))}
      </div>
      <div className="border-t p-3">
        <Skeleton className="h-10" />
      </div>
    </div>
  );
}

function Conversation({
  refs,
  title,
  unread,
  flagged,
  onKeys,
  draft,
  myAddress,
  onBack,
  onFlag,
  onMarkUnread,
  onGone,
  onCompose,
  onSetupSignature,
  moveMenu,
}: {
  refs: Ref[];
  /** Тема з рядка списку — щоб заголовок був одразу, ще до завантаження листів. */
  title?: string;
  unread: number;
  flagged?: boolean;
  /** Розмова реєструє дії для клавіатурних скорочень (null — розмову закрито). */
  onKeys: (keys: ConversationKeys | null) => void;
  /** Збережена незакінчена відповідь у цій розмові. */
  draft: Ref | null;
  myAddress: string;
  onBack: () => void;
  onFlag?: (flagged: boolean) => void;
  onMarkUnread?: () => void;
  /** Розмова пішла в кошик цілком — прибрати її зі списку. */
  onGone: () => void;
  onCompose: (d: ComposeDraft) => void;
  onSetupSignature: () => void;
  /** «Перемістити в папку» — у шапці розмови. */
  moveMenu?: ReactNode;
}) {
  const account = useMailAccount();
  const t = uk.mail;
  const qc = useQueryClient();
  const folders = useQuery(trpc.mail.folders.queryOptions({ account }));
  const thread = useQuery({
    ...trpc.mail.thread.queryOptions({ account, refs }),
    placeholderData: keepPreviousData,
  });
  const [replyTo, setReplyTo] = useState<Ref | null>(null);
  const [replyNonce, setReplyNonce] = useState(0);
  const [replyAll, setReplyAll] = useState(false);
  // Чернетку беремо ту, що була при відкритті: далі поле відповіді зберігає її само.
  const [draftRef] = useState(draft);
  const saved = useQuery({
    ...trpc.mail.draft.queryOptions(draftRef ? { account, uid: draftRef.uid } : skipToken),
    // Завжди свіжа версія: стара могла бути вже замінена автозбереженням.
    staleTime: 0,
    gcTime: 0,
  });
  const [useDraftTarget, setUseDraftTarget] = useState(true);
  const [flash, setFlash] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const refresh = () => qc.invalidateQueries(trpc.mail.pathFilter());

  const messages = thread.data ?? [];
  const count = messages.length;

  // Прокрутка як у чаті: розмова відкривається на останньому листі (до відмальовування —
  // без стрибка); новий лист прокручує донизу, лише якщо читаєте внизу, інакше — кнопка.
  const [atBottom, setAtBottom] = useState(true);
  const [readCount, setReadCount] = useState(0);
  const pinnedUntil = useRef(0);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setAtBottom(bottom);
    if (bottom) setReadCount(count);
  };
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !count || !atBottom) return;
    const first = readCount === 0;
    if (first) pinnedUntil.current = Date.now() + 1500;
    el.scrollTo({ top: el.scrollHeight, behavior: first ? 'instant' : 'smooth' });
  }, [count, atBottom, readCount]);
  // Картинки в листах довантажуються й збільшують висоту — перші миті тримаємо низ.
  useEffect(() => {
    const el = scroller.current;
    const inner = el?.firstElementChild;
    if (!el || !inner) return;
    const ro = new ResizeObserver(() => {
      if (Date.now() < pinnedUntil.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [count]);
  const newBelow = readCount > 0 && count > readCount && !atBottom;

  // Сервер позначив листи прочитаними — оновлюємо лічильники.
  useEffect(() => {
    if (thread.isSuccess && unread > 0) {
      void qc.invalidateQueries(trpc.mail.threads.pathFilter());
      void qc.invalidateQueries(trpc.mail.folders.pathFilter());
      void qc.invalidateQueries(trpc.mail.unread.pathFilter());
    }
  }, [thread.isSuccess, unread, qc]);

  const unseen = useMutation(
    trpc.mail.setSeen.mutationOptions({
      onSuccess: () => {
        onBack();
        void refresh();
      },
      onError: (e) => toast.error(errorMessage(e)),
    }),
  );
  const trash = useMutation(
    trpc.mail.trash.mutationOptions({
      onSuccess: (r, v) => {
        toast.success(
          r.deleted ? t.deleted(r.deleted) : v.refs.length > 1 ? t.threadTrashed : t.trashed,
        );
        if (v.refs.length >= messages.length) {
          onGone();
          onBack();
        }
        void refresh();
      },
      onError: (e) => toast.error(errorMessage(e)),
    }),
  );

  // Листи з Кошика видаляються остаточно — лише після підтвердження.
  const trashPath = folders.data?.find((f) => f.role === 'trash')?.path;
  const [confirmRefs, setConfirmRefs] = useState<Ref[] | null>(null);
  const remove = (list: Ref[]) => {
    if (trashPath && list.every((r) => r.folder === trashPath)) setConfirmRefs(list);
    else trash.mutate({ account, refs: list });
  };

  const first = messages[0];
  const draftId = saved.data?.inReplyTo;
  const draftTarget =
    useDraftTarget && draftId
      ? messages.find((m) => m.messageId && m.messageId === draftId)
      : undefined;
  const chosen = (replyTo ? messages.find((m) => same(m, replyTo)) : undefined) ?? draftTarget;
  // Типово: розсилка на кількох (більше двох отримувачів, крім себе) — відповідь на кореневий
  // лист розмови; листування з однією-двома людьми — на останній лист співрозмовника.
  const rootRecipients = new Set(
    [...(first?.to ?? []), ...(first?.cc ?? [])]
      .map((p) => p.address)
      .filter((a) => a !== myAddress),
  );
  const target =
    chosen ??
    (rootRecipients.size > 2 ? first : (messages.findLast((m) => !m.mine) ?? messages.at(-1)));
  const startReply = (m: Message, everyone: boolean) => {
    setReplyTo({ folder: m.folder, uid: m.uid });
    setReplyAll(everyone);
    setReplyNonce((n) => n + 1);
  };
  const forward = (m: Message) =>
    onCompose({
      subject: /^fwd?:/i.test(m.subject) ? m.subject : `Fwd: ${m.subject}`,
      quote: quote(m, t.forwarded(header(m))),
    });
  const trashAll = () => remove(messages.map((m) => ({ folder: m.folder, uid: m.uid })));

  // Клавіатура: r / a / f — до листа, якому відповідаємо; # — уся розмова.
  useEffect(() => {
    if (!target) return;
    onKeys({
      reply: () => startReply(target, false),
      replyAll: () => startReply(target, true),
      forward: () => forward(target),
      trash: trashAll,
    });
    return () => onKeys(null);
  });

  if (thread.isPending) return <ConversationSkeleton title={title} onBack={onBack} />;
  if (thread.error) return <p className="text-sm text-destructive">{errorMessage(thread.error)}</p>;
  if (!first || !target) return <EmptyState icon={MailIcon} title={t.notFound} />;

  const inTrash = Boolean(trashPath) && messages.every((m) => m.folder === trashPath);
  const byRef = new Map(messages.map((m) => [refKey(m), m]));
  // Імена учасників з усіх листів розмови: адресат без імені в одному листі — з іменем з іншого.
  const names = new Map<string, string>();
  for (const m of messages)
    for (const p of [...m.from, ...m.to, ...m.cc])
      if (p.name || !names.has(p.address))
        names.set(p.address, p.name || names.get(p.address) || '');
  const others = [...names.keys()].filter((a) => a !== myAddress);

  const jump = (r: Ref) => {
    const id = refKey(r);
    document.getElementById(`msg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(id);
    window.setTimeout(() => setFlash((f) => (f === id ? null : f)), 1500);
  };

  return (
    <div className="flex h-full min-h-96 flex-col rounded-lg border">
      <AlertDialog open={confirmRefs !== null} onOpenChange={(o) => !o && setConfirmRefs(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteForever}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deleteForeverHint(confirmRefs?.length ?? 0)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{uk.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => confirmRefs && trash.mutate({ account, refs: confirmRefs })}
            >
              {t.deleteForever}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <header className="flex items-start gap-2 border-b px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label={t.back}
          onClick={onBack}
        >
          <ChevronLeftIcon />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold">{first.subject || t.noSubject}</h2>
          <p className="truncate text-xs text-muted-foreground duration-200 animate-in fade-in-0">
            {t.members(others.map((a) => names.get(a) || a).join(', ') || t.onlyMe, count)}
          </p>
        </div>
        {onFlag && (
          <Button
            variant="ghost"
            size="icon"
            title={`${flagged ? t.unflag : t.flag} (s)`}
            aria-label={flagged ? t.unflag : t.flag}
            aria-pressed={flagged}
            onClick={() => onFlag(!flagged)}
          >
            <StarIcon
              className={cn('transition-colors', flagged && 'fill-amber-400 text-amber-500')}
            />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          title={`${t.markUnread} (Shift+U)`}
          aria-label={t.markUnread}
          disabled={unseen.isPending}
          onClick={() =>
            onMarkUnread
              ? onMarkUnread()
              : unseen.mutate({ account, refs: [refs.at(-1)!], seen: false })
          }
        >
          <MailIcon />
        </Button>
        {moveMenu}
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive"
          title={`${inTrash ? t.threadDelete : t.threadToTrash} (#)`}
          aria-label={inTrash ? t.threadDelete : t.threadToTrash}
          disabled={trash.isPending}
          onClick={trashAll}
        >
          <Trash2Icon />
        </Button>
      </header>

      <div className="relative min-h-0 flex-1">
        <div
          ref={scroller}
          onScroll={onScroll}
          className="h-full overflow-x-hidden overflow-y-auto bg-muted/30 px-2 py-4 sm:px-3"
        >
          <div className="space-y-3 duration-300 animate-in fade-in-0">
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const newDay = m.date && (!prev?.date || day(prev.date) !== day(m.date));
              const parent = m.parent ? byRef.get(refKey(m.parent)) : undefined;
              return (
                <div
                  key={refKey(m)}
                  className={cn(
                    i >= readCount &&
                      readCount > 0 &&
                      'duration-300 animate-in fade-in-0 slide-in-from-bottom-2',
                  )}
                >
                  {newDay && (
                    <div className="my-2 flex justify-center">
                      <span className="rounded-full bg-background px-3 py-0.5 text-xs text-muted-foreground shadow-xs">
                        {dayLabel(m.date!)}
                      </span>
                    </div>
                  )}
                  <Bubble
                    m={m}
                    myAddress={myAddress}
                    names={names}
                    parent={parent}
                    parentIsPrevious={Boolean(parent && prev && same(parent, prev))}
                    flash={flash === refKey(m)}
                    replying={target === m}
                    onJump={jump}
                    onReply={(everyone) => startReply(m, everyone)}
                    onForward={() => forward(m)}
                    inTrash={m.folder === trashPath}
                    onTrash={() => remove([{ folder: m.folder, uid: m.uid }])}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          tabIndex={newBelow ? 0 : -1}
          aria-hidden={!newBelow}
          onClick={() =>
            scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })
          }
          className={cn(
            'absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg transition-all duration-200',
            newBelow ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
          )}
        >
          <ArrowDownIcon className="size-3.5" /> {t.newMessages}
        </button>
      </div>

      {draftRef && saved.isPending ? (
        <div className="border-t p-3">
          <Skeleton className="h-10" />
        </div>
      ) : (
        <ReplyBox
          target={target}
          explicit={Boolean(chosen)}
          all={replyAll}
          onAllChange={setReplyAll}
          openToken={replyNonce}
          subject={first.subject}
          myAddress={myAddress}
          names={names}
          initialDraft={saved.data ?? null}
          onJump={() => jump(target)}
          onClear={() => {
            setReplyTo(null);
            setUseDraftTarget(false);
          }}
          onSent={() => {
            setReplyTo(null);
            setUseDraftTarget(false);
            void refresh();
          }}
          onPopOut={(d) => {
            setUseDraftTarget(false);
            onCompose(d);
          }}
          onSetupSignature={onSetupSignature}
        />
      )}
    </div>
  );
}

/** Оформлення HTML листа (і процитованих листів) у бульбашці. */
const MAIL_HTML =
  'prose-editor max-w-none overflow-x-auto text-sm [&_img]:inline-block [&_img]:h-auto [&_img]:max-w-full [&_table]:w-auto [&_td]:pr-3 [&_td]:align-top';

type Quoted = Message['history'][number];

/**
 * Попереднє листування з цитати листа: кожен процитований лист — окрема картка
 * (хто, коли, кому, тема, текст) замість вкладених одна в одну цитат.
 */
function QuotedHistory({
  history,
  me,
  names,
  onOriginal,
}: {
  history: Quoted[];
  me: string;
  names: Map<string, string>;
  onOriginal: () => void;
}) {
  const t = uk.mail;
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        className="flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        <HistoryIcon className="size-3.5" />
        {open ? t.hideHistory : t.history(history.length)}
        <ChevronDownIcon className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <>
          <ol className="mt-2 space-y-2 duration-200 animate-in fade-in-0 slide-in-from-top-1">
            {history.map((q, i) => (
              <QuotedCard key={i} q={q} me={me} names={names} />
            ))}
          </ol>
          <button
            type="button"
            className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={onOriginal}
          >
            {t.showOriginal}
          </button>
        </>
      )}
    </div>
  );
}

function QuotedCard({ q, me, names }: { q: Quoted; me: string; names: Map<string, string> }) {
  const t = uk.mail;
  const from = q.from?.address
    ? { address: q.from.address, name: q.from.name || names.get(q.from.address) || '' }
    : null;
  const title = from
    ? from.address === me
      ? t.you
      : nameOf(from)
    : q.from?.name || q.header || '—';
  return (
    <li className="rounded-xl border bg-muted/40 px-3 py-2">
      <div className="flex items-start gap-2">
        <Avatar person={from} className="size-7 text-[10px]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-medium" title={from?.address}>
              {title}
            </span>
            {from && from.address !== me && from.name && (
              <span className="text-xs text-muted-foreground">{from.address}</span>
            )}
            {q.sentAt ? (
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {formatDateTime(q.sentAt)}
              </span>
            ) : (
              q.date && (
                <span
                  className="ml-auto text-xs text-muted-foreground tabular-nums"
                  title={q.when ? q.date : undefined}
                >
                  {q.when ?? q.date}
                </span>
              )
            )}
          </div>
          <div className="mt-0.5 flex flex-col text-xs text-muted-foreground">
            <People label={t.to} list={q.to} me={me} names={names} />
            <People label={t.cc} list={q.cc} me={me} names={names} />
            {q.subject && (
              <div className="truncate">
                <span className="text-muted-foreground/70">{t.subject}: </span>
                {q.subject}
              </div>
            )}
          </div>
        </div>
      </div>
      {q.html !== null ? (
        <div
          className={cn('mt-2', MAIL_HTML)}
          // HTML очищено на сервері так само, як і сам лист.
          dangerouslySetInnerHTML={{ __html: q.html }}
        />
      ) : (
        <p className="mt-2 text-sm whitespace-pre-wrap">{q.text}</p>
      )}
    </li>
  );
}

function People({
  label,
  list,
  me,
  names,
}: {
  label: string;
  list: Person[];
  me: string;
  names: Map<string, string>;
}) {
  const [all, setAll] = useState(false);
  // Усі адресати, поки вміщаються у два рядки; більше — згортаємо з кнопкою.
  const [overflow, setOverflow] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setOverflow(el.scrollHeight > el.clientHeight + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [list.length, all]);
  if (!list.length) return null;
  return (
    <div>
      <div ref={box} className={cn(!all && 'line-clamp-2')}>
        <span className="text-muted-foreground/70">{label}: </span>
        {list.map((p, i) => (
          <span key={p.address} title={p.address}>
            {i > 0 && ', '}
            {p.address === me ? uk.mail.you : p.name || names.get(p.address) || p.address}
          </span>
        ))}
      </div>
      {(all || overflow) && (
        <button
          type="button"
          className="underline-offset-2 hover:underline"
          onClick={() => setAll((v) => !v)}
        >
          {all ? uk.mail.showLess : uk.mail.showAllRecipients(list.length)}
        </button>
      )}
    </div>
  );
}

function Bubble({
  m,
  myAddress,
  names,
  parent,
  parentIsPrevious,
  flash,
  replying,
  onJump,
  onReply,
  onForward,
  inTrash,
  onTrash,
}: {
  m: Message;
  myAddress: string;
  names: Map<string, string>;
  parent: Message | undefined;
  parentIsPrevious: boolean;
  flash: boolean;
  replying: boolean;
  onJump: (r: Ref) => void;
  onReply: (all: boolean) => void;
  onForward: () => void;
  /** Лист у Кошику: «Видалити» — остаточно. */
  inTrash: boolean;
  onTrash: () => void;
}) {
  const account = useMailAccount();
  const t = uk.mail;
  // Оригінал: лист разом із цитатою так, як його надіслали (нічого не прибрано).
  const [original, setOriginal] = useState(false);
  const preview = usePreview();
  // «Відповісти всім» — коли, крім відправника й себе, у листі є ще хтось.
  const manyPeople =
    new Set([...m.from, ...m.to, ...m.cc].map((p) => p.address).filter((x) => x !== myAddress))
      .size > 1;
  const sender = m.from[0] ?? null;
  return (
    <article
      id={`msg-${refKey(m)}`}
      className={cn('group flex gap-2', m.mine && 'flex-row-reverse')}
      aria-label={t.messageFrom(sender ? nameOf(sender) : '')}
    >
      <Avatar person={sender} className="mt-1 hidden sm:flex" />
      <div
        className={cn(
          'flex max-w-[92%] min-w-0 flex-col sm:max-w-[min(42rem,85%)]',
          m.mine && 'items-end',
        )}
      >
        <div
          className={cn(
            'max-w-full min-w-0 rounded-2xl border px-3 py-2 shadow-xs transition-shadow',
            m.mine
              ? 'rounded-tr-sm border-primary/20 bg-primary/10'
              : 'rounded-tl-sm bg-background',
            flash && 'ring-2 ring-primary',
            replying && 'border-primary/50',
          )}
        >
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-semibold" title={sender?.address}>
              {m.mine ? t.you : sender ? nameOf(sender) : '—'}
            </span>
            {!m.mine && sender?.name && (
              <span className="text-xs text-muted-foreground">{sender.address}</span>
            )}
            <span
              className="ml-auto text-xs text-muted-foreground tabular-nums"
              title={m.date ? formatDateTime(m.date) : ''}
            >
              {m.date ? time(m.date) : ''}
            </span>
          </div>
          <div className="mt-0.5 flex flex-col text-xs text-muted-foreground">
            <People label={t.to} list={m.to} me={myAddress} names={names} />
            <People label={t.cc} list={m.cc} me={myAddress} names={names} />
            <People label={t.bcc} list={m.bcc} me={myAddress} names={names} />
          </div>

          {parent && (
            <button
              type="button"
              onClick={() => onJump(parent)}
              className="mt-2 flex w-full min-w-0 items-start gap-1.5 rounded-md border-l-2 border-primary bg-muted/60 px-2 py-1 text-left text-xs hover:bg-muted"
              title={t.goToParent}
            >
              <CornerDownRightIcon className="mt-0.5 size-3.5 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="font-medium">
                  {parentIsPrevious
                    ? t.replyToAbove(
                        parent.mine ? t.you : nameOf(parent.from[0] ?? { name: '', address: '' }),
                      )
                    : t.replyToFrom(
                        parent.mine ? t.you : nameOf(parent.from[0] ?? { name: '', address: '' }),
                        parent.date ? formatDateTime(parent.date) : '',
                      )}
                </span>
                {parent.snippet && (
                  <span className="block truncate text-muted-foreground">{parent.snippet}</span>
                )}
              </span>
            </button>
          )}

          {m.html !== null ? (
            <div
              key={original ? 'original' : 'short'}
              className={cn('mt-2 duration-200 animate-in fade-in-0', MAIL_HTML)}
              // HTML листа очищено на сервері (білий список тегів, без скриптів і зображень).
              dangerouslySetInnerHTML={{ __html: original && m.fullHtml ? m.fullHtml : m.html }}
            />
          ) : (
            <p className="mt-2 text-sm whitespace-pre-wrap">
              {original && m.fullText ? m.fullText : m.text}
            </p>
          )}
          {m.history.length > 0 && !original ? (
            <QuotedHistory
              history={m.history}
              me={myAddress}
              names={names}
              onOriginal={() => setOriginal(true)}
            />
          ) : (
            (m.fullHtml || m.fullText) && (
              <button
                type="button"
                className="mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setOriginal((v) => !v)}
              >
                {original ? (m.history.length ? t.hideOriginal : t.hideQuote) : t.showQuote}
              </button>
            )
          )}

          {m.attachments.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1">
              {m.attachments.map((a) => (
                <AttachmentCard
                  key={a.part}
                  url={`/api/mail/attachment?${new URLSearchParams({ folder: m.folder, uid: String(m.uid), part: a.part, ...(account ? { account } : {}) })}`}
                  name={a.filename}
                  contentType={a.contentType}
                  size={a.size}
                  onPreview={(url) => preview({ url, name: a.filename })}
                />
              ))}
            </ul>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-0.5 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onReply(false)}
          >
            <ReplyIcon /> {t.reply}
          </Button>
          {manyPeople && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onReply(true)}
            >
              <ReplyAllIcon /> {t.replyAll}
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onForward}>
            <ForwardIcon /> {t.forward}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-destructive"
            onClick={onTrash}
          >
            <Trash2Icon /> {inTrash ? t.deleteForever : t.toTrash}
          </Button>
        </div>
      </div>
    </article>
  );
}

/** Вкладення листа — компактно: іконка типу, назва, розмір; клік — перегляд, стрілка — завантажити. */
function AttachmentCard({
  url,
  name,
  contentType,
  size,
  onPreview,
}: {
  url: string;
  name: string;
  contentType: string;
  size: number;
  onPreview: (url: string) => void;
}) {
  const t = uk.mail;
  return (
    <li className="group/att flex h-7 max-w-64 min-w-0 items-center rounded-md border bg-background text-xs transition-colors hover:border-primary/30 hover:bg-muted/40">
      <button
        type="button"
        title={`${t.preview}: ${name}`}
        className="flex h-full min-w-0 items-center gap-1.5 pr-1 pl-1 text-left"
        onClick={() => onPreview(`${url}&inline=1`)}
      >
        <FileIcon name={name} contentType={contentType} className="size-5" />
        <span className="min-w-0 truncate font-medium">{name}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
          {fileSize(size)}
        </span>
      </button>
      <a
        href={url}
        title={t.download}
        aria-label={`${t.download}: ${name}`}
        className="flex h-full shrink-0 items-center rounded-r-md px-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <DownloadIcon className="size-3.5" />
      </a>
    </li>
  );
}

/** Поле відповіді внизу розмови: відповідь усім учасникам вибраного листа. */
function ReplyBox({
  target,
  explicit,
  all,
  onAllChange,
  openToken,
  subject,
  myAddress,
  names,
  initialDraft,
  onJump,
  onClear,
  onSent,
  onPopOut,
  onSetupSignature,
}: {
  target: Message;
  explicit: boolean;
  /** Відповідь усім учасникам листа чи лише відправнику. */
  all: boolean;
  onAllChange: (all: boolean) => void;
  /** Змінюється при кожному «Відповісти» на листі — тоді згорнуте поле розгортається. */
  openToken: number;
  subject: string;
  myAddress: string;
  names: Map<string, string>;
  initialDraft: {
    uid: number;
    html: string;
    to: string[];
    cc: string[];
    bcc: string[];
  } | null;
  onJump: () => void;
  onClear: () => void;
  onSent: () => void;
  /** Перенести відповідь у вікно листа (там більше місця; розмову можна гортати). */
  onPopOut: (d: ComposeDraft) => void;
  onSetupSignature: () => void;
}) {
  const account = useMailAccount();
  const t = uk.mail;
  // Адресати — від листа, на який відповідаємо; змінені вручну — лише для цього листа.
  // Текст відповіді лишається, навіть якщо тим часом прийшов новий лист.
  // «Відправнику» і «Усім» — сталі набори; змінені вручну адресати — «Свій список» (для цього листа).
  const targetKey = refKey(target);
  const initial = replyRecipients(target, myAddress, all);
  const everyone = replyRecipients(target, myAddress, true);
  const everyoneCount = new Set([...everyone.to, ...everyone.cc]).size;
  const canReplyAll = everyoneCount > replyRecipients(target, myAddress, false).to.length;
  const [edited, setEdited] = useState<{ key: string; to: string; cc: string; bcc: string } | null>(
    () =>
      initialDraft
        ? {
            key: targetKey,
            to: initialDraft.to.join(', '),
            cc: initialDraft.cc.join(', '),
            bcc: initialDraft.bcc.join(', '),
          }
        : null,
  );
  const own = edited?.key === targetKey ? edited : null;
  const to = own?.to ?? initial.to.join(', ');
  const cc = own?.cc ?? initial.cc.join(', ');
  // Прихована копія не успадковується з листа, на який відповідаємо, — лише своя.
  const bcc = own?.bcc ?? '';
  const [editing, setEditing] = useState(false);
  const edit = (patch: { to?: string; cc?: string; bcc?: string }) =>
    setEdited({ key: targetKey, to, cc, bcc, ...patch });
  const same = (a: string, b: string[]) => {
    const x = splitAddresses(a);
    return x.length === b.length && x.every((v) => b.includes(v));
  };
  const custom =
    own !== null && !(same(own.to, initial.to) && same(own.cc, initial.cc) && !own.bcc.trim());
  /** «Відправнику» / «Усім»: адресати — заново за правилом, ручні зміни відкидаються. */
  const pickPreset = (everyoneMode: boolean) => {
    setEdited(null);
    onAllChange(everyoneMode);
  };
  const noRecipients =
    !splitAddresses(to).length && !splitAddresses(cc).length && !splitAddresses(bcc).length;
  const [html, setHtml] = useState(initialDraft?.html ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [opened, setOpened] = useState(false);
  const replySubject = /^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`;
  const quoteHtml = quote(target, header(target));
  const inReplyTo = target.messageId ?? undefined;
  const references = [...target.references, ...(target.messageId ? [target.messageId] : [])];
  const autosave = useDraftAutosave(
    { to, cc, bcc, subject: replySubject, html, quoteHtml, inReplyTo, references, account },
    { initialUid: initialDraft?.uid, isEmpty: !html.replace(/<[^>]+>/g, '').trim() },
  );
  const [signature, setSignature] = useState<string | null | undefined>(undefined);
  // Згорнуте поле — один рядок, щоб розмові лишалось місце; «Відповісти» на листі розгортає.
  // «Згорнути» діє до наступного «Відповісти» на якомусь листі (openToken змінюється).
  const [collapsedAt, setCollapsedAt] = useState<number | null>(null);
  const hasText = html.replace(/<[^>]+>/g, '').trim() !== '';
  const expanded = collapsedAt !== openToken && (opened || explicit || hasText || files.length > 0);
  const fileRef = useRef<HTMLInputElement>(null);
  const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
  const canSend = to.includes('@') && html.trim() !== '' && !sending && !tooBig;
  const drop = useFileDrop((list) => {
    setFiles((prev) => [...prev, ...list]);
    setOpened(true);
    setCollapsedAt(null);
  });

  async function popOut() {
    const draftUid = await autosave.stop();
    onPopOut({
      to,
      cc,
      bcc,
      subject: replySubject,
      html,
      quote: quoteHtml,
      quoteHeader: header(target),
      quotePreview: target.html ?? `<p>${esc(target.text ?? '').replace(/\n/g, '<br>')}</p>`,
      names: [...names],
      inReplyTo,
      references,
      draftUid,
    });
    autosave.reset();
    setHtml('');
    setEdited(null);
    setEditing(false);
    setOpened(false);
  }

  async function send() {
    if (!canSend) return;
    setSending(true);
    try {
      const draftUid = await autosave.stop();
      await sendMail({
        account,
        to,
        cc,
        bcc,
        subject: replySubject,
        html,
        quoteHtml,
        signature,
        inReplyTo,
        references,
        draftUid,
        files,
      });
      toast.success(t.sent);
      autosave.reset();
      setHtml('');
      setFiles([]);
      setEdited(null);
      setEditing(false);
      setOpened(false);
      onSent();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t.sendFailed);
    } finally {
      setSending(false);
    }
  }

  const sender = target.mine ? t.you : nameOf(target.from[0] ?? { name: '', address: '' });
  return (
    <div
      className="relative space-y-2 border-t p-3"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          void send();
        }
      }}
      {...drop.handlers}
    >
      <DropOverlay show={drop.dragging} />
      <div className="flex items-start gap-2 text-xs">
        <button
          type="button"
          onClick={onJump}
          className="flex min-w-0 flex-1 items-start gap-1.5 rounded-md border-l-2 border-primary bg-muted/60 px-2 py-1 text-left hover:bg-muted"
        >
          <ReplyIcon className="mt-0.5 size-3.5 shrink-0 text-primary" />
          <span className="min-w-0">
            <span className="font-medium">{t.replyingTo(sender)}</span>
            {target.snippet && (
              <span className="block truncate text-muted-foreground">{target.snippet}</span>
            )}
          </span>
        </button>
        {explicit && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t.replyLast}
            title={t.replyLast}
            onClick={onClear}
          >
            <XIcon />
          </Button>
        )}
        {expanded && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t.collapseReply}
            title={t.collapseReply}
            onClick={() => {
              setCollapsedAt(openToken);
              setOpened(false);
              setEditing(false);
            }}
          >
            <ChevronDownIcon />
          </Button>
        )}
      </div>
      {!expanded ? (
        <button
          type="button"
          onClick={() => {
            setOpened(true);
            setCollapsedAt(null);
          }}
          className="flex w-full min-w-0 items-center gap-2 rounded-md border bg-background px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/40"
        >
          <PenSquareIcon className="size-4 shrink-0" />
          <span className="truncate">
            {hasText
              ? t.draftCollapsed(
                  html
                    .replace(/<[^>]+>/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim(),
                )
              : t.replyPlaceholder}
          </span>
        </button>
      ) : (
        <div className="space-y-2 duration-200 animate-in fade-in-0 slide-in-from-bottom-1">
          {own && editing ? (
            <div className="space-y-1 rounded-md border px-2 py-1">
              <div className="flex items-start gap-2">
                <span className="w-12 shrink-0 pt-1 text-xs text-muted-foreground">{t.to}</span>
                <RecipientInput
                  value={to}
                  onChange={(v) => edit({ to: v })}
                  names={names}
                  label={t.to}
                  placeholder={t.toHint}
                />
              </div>
              <div className="flex items-start gap-2 border-t pt-1">
                <span className="w-12 shrink-0 pt-1 text-xs text-muted-foreground">{t.cc}</span>
                <RecipientInput
                  value={cc}
                  onChange={(v) => edit({ cc: v })}
                  names={names}
                  label={t.cc}
                />
              </div>
              <div className="flex items-start gap-2 border-t pt-1" title={t.bccHint}>
                <span className="w-12 shrink-0 pt-1 text-xs text-muted-foreground">{t.bcc}</span>
                <RecipientInput
                  value={bcc}
                  onChange={(v) => edit({ bcc: v })}
                  names={names}
                  label={t.bcc}
                />
              </div>
              <div className="flex justify-end border-t pt-1">
                <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                  <CheckIcon /> {t.doneRecipients}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="flex overflow-hidden rounded-md border text-xs" role="group">
                <button
                  type="button"
                  aria-pressed={!custom && !all}
                  className={cn(
                    'px-2 py-0.5',
                    !custom && !all ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                  )}
                  onClick={() => pickPreset(false)}
                >
                  {t.replySender}
                </button>
                {canReplyAll && (
                  <button
                    type="button"
                    aria-pressed={!custom && all}
                    className={cn(
                      'border-l px-2 py-0.5',
                      !custom && all ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                    )}
                    onClick={() => pickPreset(true)}
                  >
                    {t.replyEveryone(everyoneCount)}
                  </button>
                )}
                <button
                  type="button"
                  aria-pressed={custom}
                  className={cn(
                    'border-l px-2 py-0.5',
                    custom ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                  )}
                  onClick={() => {
                    edit({});
                    setEditing(true);
                  }}
                >
                  {t.replyCustom}
                </button>
              </div>
              {noRecipients && <span className="text-xs text-destructive">{t.noRecipients}</span>}
              <RecipientSummary label={t.to} list={splitAddresses(to)} names={names} />
              <RecipientSummary label={t.cc} list={splitAddresses(cc)} names={names} />
              <RecipientSummary label={t.bcc} list={splitAddresses(bcc)} names={names} />
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => {
                  edit({});
                  setEditing(true);
                }}
              >
                {t.changeRecipients}
              </button>
            </div>
          )}
          <RichTextField
            value={html}
            onChange={setHtml}
            placeholder={t.replyPlaceholder}
            editorClassName="min-h-16 max-h-60 overflow-y-auto"
            autoFocus
          />
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
              <PaperclipIcon /> {t.attach}
            </Button>
            {files.length > 0 && (
              <ul className="flex flex-wrap gap-1">
                {files.map((f, i) => (
                  <FileChip
                    key={`${f.name}:${i}`}
                    file={f}
                    tooBig={f.size > MAX_MB * 1024 * 1024}
                    onRemove={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  />
                ))}
              </ul>
            )}
            {tooBig && (
              <span className="text-xs text-destructive">{t.tooBig(tooBig.name, MAX_MB)}</span>
            )}
            <SignaturePicker
              account={account}
              value={signature}
              onChange={setSignature}
              onSetup={onSetupSignature}
            />
            <div className="ml-auto flex min-w-0 items-center gap-1">
              <SaveState status={autosave.status} savedAt={autosave.savedAt} />
              <Button
                variant="ghost"
                size="icon"
                title={t.openInWindow}
                aria-label={t.openInWindow}
                onClick={() => void popOut()}
              >
                <ExternalLinkIcon />
              </Button>
            </div>
            <Button disabled={!canSend} title={t.sendHotkey} onClick={() => void send()}>
              <SendIcon /> {sending ? t.sending : t.send}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
